// components/QuestionnaireModal.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ClipboardIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

//http://localhost:5050
//https://cloterenotaire.onrender.com

export default function QuestionnaireModal({
  open,
  onClose,
  id_action,
  apiBase = API_BASE,
  title = "Inventaire",
}) {
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open || !id_action) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(`${apiBase}/api/getQuestionnaire`, { params: { id_action } });
        if (!cancelled) {
          if (data?.ok && Array.isArray(data.records) && data.records.length > 0) {
            setRow(data.records[0]); // la plus récente
          } else {
            setRow(null);
            if (data?.error) setError(data.error);
          }
        }
      } catch (e) {
        console.error("❌ fetch questionnaire:", e);
        if (!cancelled) setError("Impossible de charger le questionnaire.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, id_action, apiBase]);

  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    } catch (err) {
      console.error("copy error", err);
    }
  };

  const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
  const fmtBool = (b) => (b ? "Oui" : b === false ? "Non" : "—");

  // --- SITUATIONS (keys exactes fournies)
  // {celibataire, concuinage, marie, pacs_vigueur, veuf, instance_divorce, divorce_jugement, separe_pacs_denonce}
  const sit = (row?.situation || "").trim().toLowerCase();
  const isCelibataire       = sit === "celibataire";
  const isConcubinage       = sit === "concuinage"; // orthographe telle que fournie
  const isMarie             = sit === "marie";
  const isPacsActif         = sit === "pacs" || sit === "pacs_vigueur"; // ✅ couvre 'pacs'
  const isPacsDenonce       = sit === "separe_pacs_denonce";
  const isVeuf              = sit === "veuf";
  const isInstanceDivorce   = sit === "instance_divorce";
  const isDivorceJugement   = sit === "divorce_jugement";
  const isSeparePacsDenonce = sit === "separe_pacs_denonce";
  const isDivorce           = isInstanceDivorce || isDivorceJugement;

  // Conjoint présent ?
  const hasConjointData = [
    row?.conjoint_nom,
    row?.conjoint_prenom,
    row?.conjoint_second_prenom,
    row?.conjoint_date_naissance,
    row?.conjoint_lieu_naissance,
    row?.conjoint_nationalite,
    row?.conjoint_profession,
  ].some(hasValue);

  // --- Sections de base
  const sectionInfos = [
    { key: "nom", label: "Nom", value: row?.nom },
    { key: "prenom", label: "Prénom", value: row?.prenom },
    { key: "second_prenom", label: "Second prénom", value: row?.second_prenom },
    { key: "date_naissance", label: "Date de naissance", value: row?.date_naissance },
    { key: "lieu_naissance", label: "Lieu de naissance", value: row?.lieu_naissance },
    { key: "pays_naissance", label: "Pays de naissance", value: row?.pays_naissance },
    { key: "profession", label: "Profession", value: row?.profession },
  ].filter((f) => hasValue(f.value));

  const sectionCoord = [
    { key: "adresse", label: "Adresse", value: row?.adresse },
    { key: "telephone", label: "Téléphone", value: row?.telephone },
    { key: "email", label: "Email", value: row?.email },
  ].filter((f) => hasValue(f.value));

  const sectionSituation = [
    { key: "situation", label: "Situation", value: row?.situation },
  ].filter((f) => hasValue(f.value));

  // --- Sections conditionnelles (PACS inclut date/lieu + dénoncé)
  const sectionMariage = [
    { key: "date_mariage", label: "Date de mariage", value: row?.date_mariage },
    { key: "lieu_mariage", label: "Lieu de mariage", value: row?.lieu_mariage },
    { key: "contrat_has", label: "Contrat de mariage", value: fmtBool(row?.contrat_has) },
    { key: "contrat_regime", label: "Régime matrimonial", value: row?.contrat_regime },
    { key: "contrat_notaire", label: "Notaire du contrat", value: row?.contrat_notaire },
    { key: "contrat_date", label: "Date du contrat", value: row?.contrat_date },
  ].filter((f) => hasValue(f.value) && f.value !== "—");

  // ✅ PACS EN VIGUEUR : date_pacs + lieu_pacs
  const sectionPacsActif = [
    { key: "date_pacs", label: "Date du PACS", value: row?.date_pacs },
    { key: "lieu_pacs", label: "Lieu du PACS", value: row?.lieu_pacs },
  ].filter((f) => hasValue(f.value));

  // ✅ PACS DÉNONCÉ : pacs_denonce_* (et on peut aussi garder date/lieu si tu veux)
  const sectionPacsDenonce = [
    { key: "pacs_denonce_date_jugement", label: "PACS dénoncé – date du jugement", value: row?.pacs_denonce_date_jugement },
    { key: "pacs_denonce_lieu_tribunal", label: "PACS dénoncé – tribunal", value: row?.pacs_denonce_lieu_tribunal },
  ].filter((f) => hasValue(f.value));

  const sectionDivorce = [
    { key: "divorce_date_jugement", label: "Divorce – date du jugement", value: row?.divorce_date_jugement },
    { key: "divorce_tribunal", label: "Divorce – tribunal", value: row?.divorce_tribunal },
  ].filter((f) => hasValue(f.value));

  const sectionConjoint = [
    { key: "conjoint_nom", label: "Nom du conjoint", value: row?.conjoint_nom },
    { key: "conjoint_prenom", label: "Prénom du conjoint", value: row?.conjoint_prenom },
    { key: "conjoint_second_prenom", label: "Second prénom du conjoint", value: row?.conjoint_second_prenom },
    { key: "conjoint_date_naissance", label: "Date de naissance du conjoint", value: row?.conjoint_date_naissance },
    { key: "conjoint_lieu_naissance", label: "Lieu de naissance du conjoint", value: row?.conjoint_lieu_naissance },
    { key: "conjoint_nationalite", label: "Nationalité du conjoint", value: row?.conjoint_nationalite },
    { key: "conjoint_profession", label: "Profession du conjoint", value: row?.conjoint_profession },
  ].filter((f) => hasValue(f.value));

  // --- Visibilité (conformes à tes règles)
  const showMariage     = isMarie && sectionMariage.length > 0 && !isDivorce;
  const showPacsActif   = isPacsActif && sectionPacsActif.length > 0 && !isDivorce && !isMarie;
  const showPacsDenonce = isPacsDenonce && sectionPacsDenonce.length > 0 && !isDivorce && !isMarie;
  const showDivorce     = isDivorce && sectionDivorce.length > 0;
  const showConjoint    =
    ((isMarie || isPacsActif || isConcubinage) && !isDivorce && !isPacsDenonce) &&
    hasConjointData && sectionConjoint.length > 0;

  // --- Export CSV (exporte uniquement les champs visibles)
  function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
  function toCsv(rows) {
    const esc = (s) => {
      const v = String(s ?? "");
      return /[;\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const lines = [
      ["Intitulé", "Réponse"].join(";"),
      ...rows.map(({ label, value }) => [esc(label), esc(value)].join(";")),
    ];
    return lines.join("\n");
  }
  const getVisibleItemsFlat = () => {
    if (!row) return [];
    const flat = [];
    const push = (arr) => arr?.forEach((it) => flat.push({ label: it.label, value: it.value }));
    push(sectionInfos);
    push(sectionCoord);
    push(sectionSituation);
    if (showMariage)     push(sectionMariage);
    if (showPacsActif)   push(sectionPacsActif);
    if (showPacsDenonce) push(sectionPacsDenonce);
    if (showDivorce)     push(sectionDivorce);
    if (showConjoint)    push(sectionConjoint);
    return flat;
  };
  const handleExportCsv = () => {
    const items = getVisibleItemsFlat();
    const csv = toCsv(items);
    const suffix = id_action ? `_${id_action}` : "";
    const now = new Date();
    const iso = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
    ].join("");
    const filename = `questionnaire_etat_civil${suffix}_${iso}.csv`;
    downloadBlob(csv, "text/csv;charset=utf-8", filename);
  };

  // --- Composant Section
  const Section = ({ title, items }) => {
    if (!items || items.length === 0) return null;
    return (
      <section className="mb-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
        <div className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white">
          <dl className="divide-y divide-gray-200">
            {items.map(({ key, label, value }) => (
              <div key={key} className="grid grid-cols-1 sm:grid-cols-12 items-start gap-3 p-4 sm:p-5">
                <dt className="sm:col-span-4 text-sm font-medium text-gray-700">{label}</dt>
                <dd className="sm:col-span-7 text-sm text-gray-900 whitespace-pre-wrap break-words">{value}</dd>
                <div className="sm:col-span-1 flex sm:justify-end">
                  <button
                    onClick={() => copyToClipboard(key, String(value ?? ""))}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    title="Copier"
                  >
                    {copied === key ? (
                      <>
                        <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                        Copié
                      </>
                    ) : (
                      <>
                        <ClipboardIcon className="h-4 w-4 text-gray-500" />
                        Copier
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </section>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold text-gray-900">Questionnaire état civil</DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCsv}
                disabled={loading || !row}
                className="btn-primary"
                title="Exporter en CSV"
              >
                Export CSV
              </button>
              <button
                onClick={onClose}
                className="rounded-md p-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
                title="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="rounded-md bg-slate-50 p-4 mb-3">
                <p className="text-sm text-slate-700 m-0">Chargement…</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-3">
                <p className="text-sm text-red-700 m-0">{error}</p>
              </div>
            )}

            {!loading && !error && !row && (
              <div className="rounded-md bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 m-0">Aucun questionnaire trouvé.</p>
              </div>
            )}

            {!loading && !error && row && (
              <>
                <Section title="Informations" items={sectionInfos} />
                <Section title="Coordonnées" items={sectionCoord} />
                <Section title="Situation" items={sectionSituation} />

                {showMariage && <Section title="Informations mariage" items={sectionMariage} />}
                {showPacsActif   && <Section title="Informations PACS" items={sectionPacsActif} />}
                {showPacsDenonce && <Section title="Informations PACS dénoncé" items={sectionPacsDenonce} />}
                {showDivorce && <Section title="Informations divorce" items={sectionDivorce} />}
                {showConjoint && <Section title="Informations conjoint" items={sectionConjoint} />}
              </>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}